import { type ReactNode } from 'react';
import { TextLink } from '@spark-web/text-link';

export enum IncomeVerificationErrorCode {
  PAYSLIP_INSUFFICIENT_COUNT = 'PAYSLIP_INSUFFICIENT_COUNT',
  PAYSLIP_VERIFICATION_FAILED = 'PAYSLIP_VERIFICATION_FAILED',
  BANK_STATEMENT_INSUFFICIENT_COVERAGE = 'BANK_STATEMENT_INSUFFICIENT_COVERAGE',
  BANK_STATEMENT_OUT_OF_DATE = 'BANK_STATEMENT_OUT_OF_DATE',
  BANK_STATEMENT_UNREADABLE = 'BANK_STATEMENT_UNREADABLE',
  NOTICE_OF_ASSESSMENT_OUT_OF_DATE = 'NOTICE_OF_ASSESSMENT_OUT_OF_DATE',
  INCOME_TAX_RETURN_OUT_OF_DATE = 'INCOME_TAX_RETURN_OUT_OF_DATE',
  DOCUMENT_TYPE_MISMATCH = 'DOCUMENT_TYPE_MISMATCH',
  DOCUMENT_UNREADABLE = 'DOCUMENT_UNREADABLE',
  MULTIPLE_DOCUMENT_TYPE_FAILED = 'MULTIPLE_DOCUMENT_TYPE_FAILED',

  PAYSLIP_NO_DATE = 'PAYSLIP_NO_DATE',
  PAYSLIP_INVALID_DATE = 'PAYSLIP_INVALID_DATE',
  PAYSLIP_DATE_OUT_OF_RANGE = 'PAYSLIP_DATE_OUT_OF_RANGE',
  BANK_STATEMENT_NO_DATE_RANGE = 'BANK_STATEMENT_NO_DATE_RANGE',
  BANK_STATEMENT_INVALID_FORMAT = 'BANK_STATEMENT_INVALID_FORMAT',
  BANK_STATEMENT_INVALID_DATES = 'BANK_STATEMENT_INVALID_DATES',
  BANK_STATEMENT_END_DATE_OUT_OF_RANGE = 'BANK_STATEMENT_END_DATE_OUT_OF_RANGE',
  UNKNOWN_DOCUMENT_TYPE = 'UNKNOWN_DOCUMENT_TYPE',
  TAX_ASSESSMENT_NO_YEAR = 'TAX_ASSESSMENT_NO_YEAR',
  NOTICE_OF_ASSESSMENT_NO_YEAR = 'NOTICE_OF_ASSESSMENT_NO_YEAR',
  TAX_ASSESSMENT_OUT_OF_DATE = 'TAX_ASSESSMENT_OUT_OF_DATE',
}

export type IncomeVerificationErrorContent = {
  title: string;
  description?: string;
  callout?: ReactNode;
  guidanceTitle?: string;
  guidanceItems?: string[];
};

const ATO_LINK = (
  <TextLink href="https://ato.gov.au" target="_blank" rel="noopener noreferrer">
    ato.gov.au
  </TextLink>
);

const PAYSLIP_VERIFICATION_FAILED_CONTENT: IncomeVerificationErrorContent = {
  title: "We couldn't verify your payslips",
  description:
    'The payslips you uploaded are either too old or could not be read as payslips. We need 2 recent payslips to verify your income.',
};

const BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT: IncomeVerificationErrorContent = {
  title: "Your bank statement doesn't cover enough time",
  description:
    'Your bank statement does not cover enough days. We need at least 90 days (3 months) of transaction history to verify your income.',
  callout:
    "Most bank apps let you export a custom date range. Export from 3 months ago to today's date to make sure it covers enough time.",
};

const BANK_STATEMENT_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your bank statement is out of date',
  description:
    'Your bank statement ends too far in the past. The most recent transaction on the statement needs to be from within the last month.',
};

const INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your income tax return is out of date',
  description:
    "The income tax return you uploaded isn't from the most recent financial year. We need a return from within the last 18 months.",
  callout: (
    <>You can access a copy of your tax return through your accountant or download it from myGov at {ATO_LINK}.</>
  ),
};

const NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT: IncomeVerificationErrorContent = {
  title: 'Your Notice of Assessment is out of date',
  description:
    "The Notice of Assessment you uploaded isn't from the most recent financial year. We need a document from within the last 18 months.",
  callout: <>You can download your Notice of Assessment from myGov at {ATO_LINK}.</>,
};

const DOCUMENT_TYPE_MISMATCH_CONTENT: IncomeVerificationErrorContent = {
  title: 'We couldn’t verify your income documents',
  description:
    "The documents you uploaded don't match the types we need for income verification. Please upload one of the following documents:",
  guidanceItems: [
    '2 payslips from the last 3 months',
    'A bank statement covering the last 90 days and ending within the last month',
    'A Notice of Assessment from the ATO from within the last 18 months',
    'An income tax return completed within the last 18 months',
  ],
};

export const INCOME_VERIFICATION_ERROR_CONTENT: Record<IncomeVerificationErrorCode, IncomeVerificationErrorContent> = {
  [IncomeVerificationErrorCode.PAYSLIP_INSUFFICIENT_COUNT]: {
    title: 'We need one more payslip',
    description: 'We found 1 payslip from the last 3 months. We need 2 to verify your income.',
  },
  [IncomeVerificationErrorCode.PAYSLIP_VERIFICATION_FAILED]: PAYSLIP_VERIFICATION_FAILED_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_NO_DATE]: PAYSLIP_VERIFICATION_FAILED_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_INVALID_DATE]: PAYSLIP_VERIFICATION_FAILED_CONTENT,
  [IncomeVerificationErrorCode.PAYSLIP_DATE_OUT_OF_RANGE]: PAYSLIP_VERIFICATION_FAILED_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INSUFFICIENT_COVERAGE]: BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_NO_DATE_RANGE]: BANK_STATEMENT_INSUFFICIENT_COVERAGE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_OUT_OF_DATE]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INVALID_FORMAT]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_INVALID_DATES]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_END_DATE_OUT_OF_RANGE]: BANK_STATEMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.BANK_STATEMENT_UNREADABLE]: {
    title: 'We couldn’t read your bank statement',
    description:
      'We were unable to extract date information from your bank statement. This can happen if the file is in an unusual format, or the key details aren’t visible.',
  },
  [IncomeVerificationErrorCode.NOTICE_OF_ASSESSMENT_OUT_OF_DATE]: NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.NOTICE_OF_ASSESSMENT_NO_YEAR]: NOTICE_OF_ASSESSMENT_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.INCOME_TAX_RETURN_OUT_OF_DATE]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.TAX_ASSESSMENT_NO_YEAR]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.TAX_ASSESSMENT_OUT_OF_DATE]: INCOME_TAX_RETURN_OUT_OF_DATE_CONTENT,
  [IncomeVerificationErrorCode.DOCUMENT_TYPE_MISMATCH]: DOCUMENT_TYPE_MISMATCH_CONTENT,
  [IncomeVerificationErrorCode.UNKNOWN_DOCUMENT_TYPE]: DOCUMENT_TYPE_MISMATCH_CONTENT,
  [IncomeVerificationErrorCode.DOCUMENT_UNREADABLE]: {
    title: 'We couldn’t verify your income documents',
    description:
      "We weren't able to read the file you uploaded. This can happen if it's blurry, corrupted, password-protected, or in a format we don't support.",
  },
  [IncomeVerificationErrorCode.MULTIPLE_DOCUMENT_TYPE_FAILED]: {
    title: 'We couldn’t verify your documents',
  },
};
